<script lang="ts" setup>
import { BackupHeater } from '@/services/models/implementations/BackupHeater'
import { onMounted, ref } from 'vue'
import i18n from '@/helper/Translations'
import { MountingType } from '@/services/models/BackupHeaterCharacteristic'
import { FuelType } from '@/helper/Types'
import { BackupHeaterFilter, BoilerType } from '@/models/BackupHeaterFilter'

const props = defineProps<{
  backupHeaters: BackupHeater[]
}>()

const emit = defineEmits<{
  (e: 'filterChanged', value: BackupHeaterFilter): void
}>()

const mountingTypes = ref<Set<MountingType>>(new Set())
const boilerTypes = ref<Set<BoilerType>>(new Set())
const fuelTypes = ref<Set<FuelType>>(new Set())

const filter = ref(new BackupHeaterFilter())

onMounted(() => {
  props.backupHeaters.forEach(backupHeater => {
    if (backupHeater.chr?.fuelType) {
      fuelTypes.value.add(backupHeater.chr.fuelType)
    }
    if (backupHeater.chr?.mounting) {
      mountingTypes.value.add(backupHeater.chr.mounting)
    }
    if (backupHeater.chr?.dhwCapable) {
      boilerTypes.value.add(BoilerType.COMBI_BOILER)
    } else {
      boilerTypes.value.add(BoilerType.SYSTEM_BOILER)
    }
  })
})

function filterByBoilerType(boilerType: BoilerType) {
  filter.value.boilerType = filter.value.boilerType === boilerType ? undefined : boilerType
  emit('filterChanged', filter.value)
}

function filterByFuelType(fuelType: FuelType) {
  filter.value.fuelType = filter.value.fuelType === fuelType ? undefined : fuelType
  emit('filterChanged', filter.value)
}

function filterByMountingType(mountingType: MountingType) {
  filter.value.mounting = filter.value.mounting === mountingType ? undefined : mountingType
  emit('filterChanged', filter.value)
}

</script>

<template>
  <div
    v-if="boilerTypes.size > 1 || mountingTypes.size > 1 || fuelTypes.size > 1"
    class="product__filter product__filter-bar card"
  >
    <div v-if="boilerTypes.size > 1" class="product__filter-buttons">
      <button
        v-for="boilerType in boilerTypes"
        :key="boilerType"
        :class="{'is--tertiary': filter.boilerType === boilerType}"
        :value="boilerType"
        class="btn has--outline is--secondary"
        tabindex="2"
        @click="filterByBoilerType(boilerType)"
      >
        {{ i18n.global.t(`step_heating_details.hybridsystem_${boilerType}`) }}
      </button>
    </div>

    <div v-if="mountingTypes.size > 1" class="product__filter-buttons">
      <button
        v-for="mountingType in mountingTypes"
        :key="mountingType"
        :class="{'is--tertiary': filter.mounting === mountingType}"
        :value="mountingType"
        class="btn has--outline is--secondary"
        tabindex="2"
        @click="filterByMountingType(mountingType)"
      >
        {{ i18n.global.t(`mounting_types.${mountingType}`) }}
      </button>
    </div>

    <div v-if="fuelTypes.size > 1" class="product__filter-buttons">
      <button
        v-for="fuelType in fuelTypes"
        :key="fuelType"
        :class="{'is--tertiary': filter.fuelType === fuelType}"
        :value="fuelType"
        class="btn has--outline is--secondary"
        tabindex="2"
        @click="filterByFuelType(fuelType)"
      >
        {{ i18n.global.t(`fuel_types.${fuelType}`) }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.product__filter {
  &-bar {
    display: flex;
    padding: 0.5rem;
    gap: 0.5rem;
    margin-bottom: 2rem;
    overflow: initial;
    border-radius: 0.25rem;

    @include media('>=desktop') {
      padding: 1rem;
      gap: 1rem;
    }

    @include media('<tablet') {
      flex-wrap: wrap;
    }
  }

  &-buttons {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;

    &:not(:first-child) {
      border-left: 1px lightgray solid;
      padding-left: 0.5rem;
    }

    @include media('>=desktop') {
      gap: 1rem;

      &:not(:first-child) {
        padding-left: 1rem;
      }

      button {
        padding-left: 1rem;
        padding-right: 1rem;
      }
    }

    button {
      min-width: 0;
      align-items: center;
      padding-left: 0.5rem;
      padding-right: 0.5rem;

      @include themify($themes) {
        border-radius: rem-calc(themed('btnSubNavBorderRadius'));
      }
    }
  }
}
</style>
